import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/Badge";
import { Plus, Trash2, Pencil, Power, X, Layers, Eye, Sparkles, RefreshCcw, Upload, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

// ---------- Quick-fill templates so admins can ship a popup in ~10 sec ----------
const TEMPLATES = [
  {
    key: "welcome",
    label: "Welcome bonus",
    sample: {
      title: "🎁 100% Welcome Bonus",
      body: "Sign up today and get up to BDT 30,000 on your first deposit. Limited time offer.",
      cta_text: "Claim now",
      cta_link: "/register",
      audience: "guest",
      style: "modal",
      background: "#0B1220",
      text_color: "#FFFFFF",
      accent_color: "#10B981",
    },
  },
  {
    key: "promo",
    label: "Live promo",
    sample: {
      title: "🔥 Friday Cashback",
      body: "Get 10% cashback on every loss this Friday. Credited automatically every Saturday.",
      cta_text: "View promo",
      cta_link: "/promotions",
      audience: "logged_in",
      style: "banner_top",
      background: "#7C2D12",
      text_color: "#FFFFFF",
      accent_color: "#F59E0B",
    },
  },
  {
    key: "announcement",
    label: "Announcement",
    sample: {
      title: "Scheduled maintenance · 2 AM",
      body: "Our servers will be briefly offline tonight from 2 AM to 2:30 AM BDT.",
      cta_text: "",
      cta_link: "",
      audience: "all",
      style: "banner_bottom",
      background: "#1F2937",
      text_color: "#FFFFFF",
      accent_color: "#3B82F6",
    },
  },
  {
    key: "toast",
    label: "App download toast",
    sample: {
      title: "📲 Install our app",
      body: "Get faster deposits, push alerts and exclusive bonuses on the mobile app.",
      cta_text: "Download",
      cta_link: "/downloads",
      audience: "all",
      style: "toast",
      background: "#0B1220",
      text_color: "#FFFFFF",
      accent_color: "#10B981",
    },
  },
];

const EMPTY = {
  title: "",
  body: "",
  image: "",
  cta_text: "",
  cta_link: "",
  audience: "all",
  placement: "home",
  style: "modal",
  background: "#ffffff",
  text_color: "#111827",
  accent_color: "#10B981",
  enabled: true,
  order: 0,
  delay_seconds: 0,
  dismissible: true,
  show_once_per_day: true,
  starts_at: "",
  ends_at: "",
};

// Convert backend ISO with timezone -> HTML datetime-local format (in BD time)
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // Get BD time string: convert UTC -> Asia/Dhaka
    const bd = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Dhaka" }));
    const pad = (n) => String(n).padStart(2, "0");
    return `${bd.getFullYear()}-${pad(bd.getMonth() + 1)}-${pad(bd.getDate())}T${pad(bd.getHours())}:${pad(bd.getMinutes())}`;
  } catch {
    return "";
  }
};

export default function Popups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(null); // popup object for fullscreen test

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/popups");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...EMPTY,
      ...row,
      starts_at: isoToLocalInput(row.starts_at),
      ends_at: isoToLocalInput(row.ends_at),
    });
    setOpen(true);
  };
  const close = () => { if (!saving) { setOpen(false); setEditing(null); setForm(EMPTY); } };

  const applyTemplate = (key) => {
    const t = TEMPLATES.find((x) => x.key === key);
    if (!t) return;
    setForm((prev) => ({ ...prev, ...t.sample }));
    toast.success(`Loaded "${t.label}" template — tweak as needed`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() && !form.image.trim() && !form.body.trim()) {
      toast.error("Add a title, body or image");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        delay_seconds: Math.max(0, Number(form.delay_seconds) || 0),
      };
      if (editing) {
        await api.patch(`/popups/${editing.id}`, payload);
        toast.success("Popup updated · live on site");
      } else {
        await api.post("/popups", payload);
        toast.success("Popup created · live on site");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/popups/${row.id}`, { enabled: !row.enabled });
      toast.success(row.enabled ? "Popup disabled" : "Popup enabled");
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete popup "${row.title || row.id}"?`)) return;
    await api.delete(`/popups/${row.id}`);
    toast.success("Deleted");
    load();
  };

  const forceReshow = async (row) => {
    try {
      await api.post(`/popups/${row.id}/force-reshow`);
      toast.success("Reshow triggered — every visitor will see this popup again");
      load();
    } catch {
      toast.error("Force reshow failed");
    }
  };

  // Test the popup full-screen (overlays admin panel briefly)
  const testFullscreen = (row) => setPreviewing(row);

  return (
    <div data-testid="popups-page">
      <PageHeader
        title="Site Popups"
        subtitle="Modal popups, banners, toasts shown to players. Pick a style → save → it goes live instantly."
        actions={(
          <button onClick={openCreate} className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5" data-testid="popup-add-btn">
            <Plus size={14} /> Add Popup
          </button>
        )}
      />

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
          <Layers size={28} className="text-gray-300" />
          <span>No popups yet. Click "Add Popup" — pick a template, hit Save, you're live.</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="popups-grid">
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`popup-card-${r.id}`}>
              <div className="h-32 relative" style={{ background: r.background || "#ffffff", color: r.text_color || "#111827" }}>
                {r.image && <img src={r.image} alt={r.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />}
                <div className="absolute inset-0 p-4 flex flex-col justify-end bg-gradient-to-t from-black/50 via-transparent">
                  <div className="font-display font-bold text-lg text-white drop-shadow">{r.title || "(no title)"}</div>
                  {r.body && <div className="text-xs text-white/90 line-clamp-2 drop-shadow">{r.body}</div>}
                </div>
                <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider font-bold bg-white/95 text-gray-800 px-2 py-0.5 rounded-sm shadow-sm">
                  {(r.style || "modal").replace("_", " ")}
                </span>
              </div>
              <div className="p-4 space-y-3">
                {/* Analytics row — Views / Clicks / CTR */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-blue-50 border border-blue-100 rounded-sm px-2 py-1.5 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">Views</div>
                    <div className="text-sm font-bold tabular text-blue-900">{(r.views || 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-sm px-2 py-1.5 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Clicks</div>
                    <div className="text-sm font-bold tabular text-emerald-900">{(r.clicks || 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-sm px-2 py-1.5 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">CTR</div>
                    <div className="text-sm font-bold tabular text-amber-900">
                      {r.views > 0 ? `${((r.clicks || 0) * 100 / r.views).toFixed(1)}%` : "—"}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                    <Badge variant={r.enabled ? "success" : "muted"}>{r.enabled ? "Enabled" : "Disabled"}</Badge>
                    <span className="capitalize">{r.audience}</span>·<span className="capitalize">{r.placement}</span>·<span>Order {r.order}</span>·<span>{fmtDate(r.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <button onClick={() => forceReshow(r)} title="Reset every visitor's 'shown today' cache — popup will re-appear for everyone right now" className="h-8 px-2 text-xs border border-violet-200 text-violet-700 rounded-sm inline-flex items-center gap-1 hover:bg-violet-50" data-testid={`popup-reshow-${r.id}`}>
                      <RefreshCcw size={12} /> Reshow
                    </button>
                    <button onClick={() => testFullscreen(r)} title="Preview full-screen" className="h-8 w-8 inline-flex items-center justify-center border border-purple-200 rounded-sm text-purple-700 hover:bg-purple-50" data-testid={`popup-preview-${r.id}`}>
                      <Eye size={12} />
                    </button>
                    <button onClick={() => toggle(r)} className={`h-8 px-2 text-xs border rounded-sm inline-flex items-center gap-1 ${r.enabled ? "border-amber-200 text-amber-700 hover:bg-amber-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`} data-testid={`popup-toggle-${r.id}`}>
                      <Power size={12} /> {r.enabled ? "Off" : "On"}
                    </button>
                    <button onClick={() => openEdit(r)} className="h-8 px-2 text-xs border border-gray-200 rounded-sm inline-flex items-center gap-1 hover:border-blue-300 hover:text-blue-700" data-testid={`popup-edit-${r.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => remove(r)} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200" data-testid={`popup-delete-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && <PopupEditor form={form} setForm={setForm} editing={editing} saving={saving} onClose={close} onSubmit={submit} applyTemplate={applyTemplate} />}
      {previewing && <FullscreenPreview popup={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

function PopupEditor({ form, setForm, editing, saving, onClose, onSubmit, applyTemplate }) {
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-5xl max-h-[94vh] overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_400px]" onClick={(e) => e.stopPropagation()} data-testid="popup-editor">

        {/* ---------- Left: form ---------- */}
        <div className="overflow-y-auto">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h3 className="font-display font-bold">{editing ? "Edit popup" : "Create popup"}</h3>
            <button onClick={onClose} className="h-8 w-8 flex items-center justify-center" data-testid="popup-editor-close"><X size={16} /></button>
          </div>

          <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="popup-form">
            {/* Quick templates */}
            <div className="bg-gradient-to-r from-emerald-50 to-purple-50 border border-emerald-100 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-2 flex items-center gap-1.5">
                <Sparkles size={11} /> Quick templates · click to load sample content
              </div>
              <div className="flex flex-wrap gap-2">
                {TEMPLATES.map((t) => (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => applyTemplate(t.key)}
                    className="h-7 px-3 text-[12px] bg-white border border-gray-200 hover:border-emerald-300 hover:text-emerald-700 rounded-full font-medium"
                    data-testid={`popup-template-${t.key}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Identity */}
            <SectionTitle>Content</SectionTitle>
            <Field label="Title" value={form.title} onChange={(v) => set("title", v)} testId="popup-title" placeholder="🎁 100% Welcome Bonus" />
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Body</label>
              <textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid="popup-body" placeholder="Up to BDT 30,000 on your first deposit" />
            </div>
            <PopupImageField value={form.image} onChange={(v) => set("image", v)} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA Button Text" value={form.cta_text} onChange={(v) => set("cta_text", v)} testId="popup-cta-text" placeholder="Claim Now" />
              <Field label="CTA Link" value={form.cta_link} onChange={(v) => set("cta_link", v)} testId="popup-cta-link" placeholder="/register" />
            </div>

            {/* Display */}
            <SectionTitle>Display</SectionTitle>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Style</label>
                <select value={form.style} onChange={(e) => set("style", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white" data-testid="popup-style">
                  <option value="modal">Centered modal</option>
                  <option value="banner_top">Top banner</option>
                  <option value="banner_bottom">Bottom banner</option>
                  <option value="toast">Bottom-right toast</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Audience</label>
                <select value={form.audience} onChange={(e) => set("audience", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white" data-testid="popup-audience">
                  <option value="all">All visitors</option>
                  <option value="guest">Guests only</option>
                  <option value="logged_in">Logged-in users</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Placement</label>
                <select value={form.placement} onChange={(e) => set("placement", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white" data-testid="popup-placement">
                  <option value="home">Homepage only</option>
                  <option value="everywhere">Every page</option>
                </select>
              </div>
            </div>

            {/* Colors */}
            <div className="grid grid-cols-4 gap-3">
              <ColorField label="Background" value={form.background} onChange={(v) => set("background", v)} testId="popup-bg" />
              <ColorField label="Text" value={form.text_color} onChange={(v) => set("text_color", v)} testId="popup-text" />
              <ColorField label="CTA / Accent" value={form.accent_color} onChange={(v) => set("accent_color", v)} testId="popup-accent" />
              <Field label="Order" type="number" value={form.order} onChange={(v) => set("order", v)} testId="popup-order" />
            </div>

            {/* Schedule */}
            <SectionTitle>Schedule (Bangladesh time)</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts at" type="datetime-local" value={form.starts_at} onChange={(v) => set("starts_at", v)} testId="popup-starts" />
              <Field label="Ends at" type="datetime-local" value={form.ends_at} onChange={(v) => set("ends_at", v)} testId="popup-ends" />
            </div>
            <p className="text-[11px] text-gray-500 -mt-2">Leave both empty to show indefinitely. Times entered here are interpreted as Bangladesh time.</p>

            {/* Behaviour */}
            <SectionTitle>Behaviour</SectionTitle>
            <div className="grid grid-cols-2 gap-3 items-end">
              <Field label="Show after delay (seconds)" type="number" value={form.delay_seconds} onChange={(v) => set("delay_seconds", v)} testId="popup-delay" placeholder="0" />
              <div className="space-y-2 pb-1">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="popup-enabled" /> Enabled (live on site)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form.dismissible} onChange={(e) => set("dismissible", e.target.checked)} data-testid="popup-dismissible" /> Dismissible (close button)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form.show_once_per_day} onChange={(e) => set("show_once_per_day", e.target.checked)} data-testid="popup-once" /> Show once / day per visitor
                </label>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-0 py-3 mt-4">
              <button type="submit" disabled={saving} className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold rounded-sm text-sm" data-testid="popup-submit">
                {saving ? "Saving…" : editing ? "Save changes · live now" : "Create popup · live now"}
              </button>
            </div>
          </form>
        </div>

        {/* ---------- Right: live preview ---------- */}
        <div className="bg-gray-100 border-l border-gray-200 flex flex-col">
          <div className="px-4 py-2 border-b border-gray-200 bg-white flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5"><Eye size={12} /> Live preview</span>
            <span className="text-[10px] text-gray-400">{form.style.replace("_", " ")}</span>
          </div>
          <div className="flex-1 overflow-hidden p-4">
            <div className="bg-gradient-to-br from-slate-900 via-slate-950 to-black h-full rounded-md relative overflow-hidden border border-slate-800">
              <PreviewRenderer popup={form} />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

function PreviewRenderer({ popup }) {
  const accent = popup.accent_color || "#10B981";
  const style = popup.style || "modal";

  if (style === "banner_top" || style === "banner_bottom") {
    const pos = style === "banner_top" ? "top-0" : "bottom-0";
    return (
      <div className={`absolute left-0 right-0 ${pos} px-3 py-2 flex items-center gap-2`} style={{ background: popup.background, color: popup.text_color }}>
        {popup.image && <img src={popup.image} alt="" className="h-8 w-8 rounded object-cover" />}
        <div className="flex-1 min-w-0">
          {popup.title && <div className="font-semibold text-[12px] truncate">{popup.title}</div>}
          {popup.body && <div className="text-[10px] opacity-90 line-clamp-1">{popup.body}</div>}
        </div>
        {popup.cta_text && <span className="text-[11px] font-bold rounded-full px-3 py-1" style={{ background: accent, color: "#0B1220" }}>{popup.cta_text}</span>}
        <X size={12} className="opacity-70" />
      </div>
    );
  }
  if (style === "toast") {
    return (
      <div className="absolute bottom-3 right-3 w-56 rounded-xl shadow-2xl overflow-hidden border border-white/10" style={{ background: popup.background, color: popup.text_color }}>
        {popup.image && <div className="h-20 overflow-hidden"><img src={popup.image} alt="" className="w-full h-full object-cover" /></div>}
        <div className="p-3">
          {popup.title && <div className="font-bold text-[12px] mb-0.5 leading-tight">{popup.title}</div>}
          {popup.body && <p className="text-[10px] opacity-90 line-clamp-2">{popup.body}</p>}
          {popup.cta_text && <button className="mt-2 w-full h-7 rounded-full text-[11px] font-bold" style={{ background: accent, color: "#0B1220" }}>{popup.cta_text}</button>}
        </div>
      </div>
    );
  }
  // modal
  return (
    <div className="absolute inset-0 flex items-center justify-center p-4">
      <div className="w-full max-w-[280px] rounded-2xl overflow-hidden shadow-2xl border border-white/10" style={{ background: popup.background, color: popup.text_color }}>
        {popup.image && <div className="h-32 overflow-hidden"><img src={popup.image} alt="" className="w-full h-full object-cover" /></div>}
        <div className="p-4">
          {popup.title && <h3 className="text-base font-bold mb-1 leading-tight">{popup.title}</h3>}
          {popup.body && <p className="text-[11px] leading-relaxed opacity-90 line-clamp-3">{popup.body}</p>}
          {popup.cta_text && <button className="mt-3 inline-flex items-center justify-center w-full h-8 rounded-full font-bold text-[12px]" style={{ background: accent, color: "#0B1220" }}>{popup.cta_text}</button>}
        </div>
      </div>
    </div>
  );
}

// Full-screen test preview overlaying the admin panel — exact same component
// the public site uses, fed a single fake item.
function FullscreenPreview({ popup, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80" onClick={onClose} data-testid="popup-fullscreen-preview">
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white rounded-full px-4 py-2 text-[12px] font-bold text-gray-900 shadow-lg flex items-center gap-2">
        <Eye size={12} /> Preview · click anywhere to close
      </div>
      <PreviewWrapper popup={popup} />
    </div>
  );
}

function PreviewWrapper({ popup }) {
  // We deliberately render the in-form PreviewRenderer at full size (no clipping)
  // so admin sees exactly what guests / users will see.
  return (
    <div className="absolute inset-0">
      <PreviewRenderer popup={popup} />
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold border-b border-gray-100 pb-1 pt-1">{children}</div>;
}

function Field({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid={testId} />
    </div>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 border border-gray-200 rounded-sm" data-testid={testId} />
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 h-10 px-2 border border-gray-200 rounded-sm text-xs font-mono" />
      </div>
    </div>
  );
}

function PopupImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fullUrl = (() => {
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    // Resolve relative API path to absolute (for preview)
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return value.startsWith("/") ? `${base}${value}` : value;
  })();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be 8 MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/popups/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded — saved to your library");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Image (optional)</label>
        <label
          className={`inline-flex items-center gap-1 h-7 px-2 text-[11px] font-semibold rounded-sm border cursor-pointer ${uploading ? "border-gray-200 text-gray-400" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
          data-testid="popup-image-upload-label"
        >
          <Upload size={11} /> {uploading ? "Uploading…" : "Upload file"}
          <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={uploading} data-testid="popup-image-upload-input" />
        </label>
      </div>
      <div className="flex items-stretch gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…  or click Upload file"
          className="flex-1 h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
          data-testid="popup-image"
        />
        {fullUrl && (
          <div className="h-10 w-10 border border-gray-200 rounded-sm overflow-hidden bg-gray-50 flex items-center justify-center" title="Preview">
            <img src={fullUrl} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
        )}
      </div>
      <div className="text-[11px] text-gray-400 mt-1">JPG · PNG · WEBP · GIF · AVIF — max 8 MB. Or paste any image URL.</div>
    </div>
  );
}

